import React, { useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Typography,
  Popover,
  useMediaQuery,
  useTheme
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import HistoryIcon from '@mui/icons-material/History';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import StatusChip from './StatusChip';

/**
 * DataTable Component
 * Config-driven reusable table component with sticky header
 * Fully responsive with mobile optimization
 */
const DataTable = ({
  columns,
  data,
  onActionClick,
  onDeleteClick,
  onCellClick,
  onEditClick,
  onHistoryClick,
  canEdit,
  page = 0,
  onPageChange,
  rowsPerPage = 10,
  onRowsPerPageChange,
  totalRows,
  mobileHiddenColumns = [] // Columns to hide on mobile
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const [requirementsPopoverAnchor, setRequirementsPopoverAnchor] = useState(null);
  const [requirementsPopoverRow, setRequirementsPopoverRow] = useState(null);

  // Filter columns based on screen size
  const getVisibleColumns = () => {
    if (isMobile) {
      // On mobile, show only essential columns + action columns
      const essentialColumns = ['sn', 'clientName', 'enquiryNumber', 'quotationNumber', 'orderDate'];
      return columns.filter(col => 
        essentialColumns.includes(col.id) || 
        col.id === 'action' || 
        col.id === 'delete' ||
        col.id === 'status'
      );
    } else if (isTablet) {
      // On tablet, hide less important columns
      return columns.filter(col => !mobileHiddenColumns.includes(col.id));
    }
    return columns;
  };

  const visibleColumns = getVisibleColumns();
  const firstColumnId = visibleColumns[0]?.id;

  const handleCellClick = (row, columnId, event) => {
    if (onCellClick && columnId !== 'action' && columnId !== 'delete') {
      onCellClick(row, columnId, event);
    }
  };

  const renderCellContent = (row, column) => {
    const value = row[column.id];

    // Custom cell renderer (e.g. for enquiry display status / next follow up)
    if (column.renderCell && typeof column.renderCell === 'function') {
      return column.renderCell(row, column);
    }

    // Special handling for status columns
    if (column.id.includes('Status') || column.id === 'status') {
      return <StatusChip status={value} />;
    }

    // Special handling for requirements column – summary + info icon (only when requirements exist)
    if (column.id === "mainProductType") {
      const requirements = row.requirements || row.requirement || [];
      if (requirements.length === 0) {
        return <Typography variant="body2" color="text.secondary">-</Typography>;
      }
      const summary = row.requirementsSummary || requirements.map(req =>
        `${req.item_name || 'Item'} (${req.item_quantity || 0})`
      ).join(', ');
      return (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          <Typography
            variant="body2"
            sx={{
              fontSize: { xs: '0.75rem', sm: '0.8125rem' },
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: isMobile && column.id !== 'sn' ? 'nowrap' : 'normal',
              maxWidth: isMobile && column.id !== 'sn' ? '120px' : 'none'
            }}
            title={summary}
          >
            {value}
            {/* {value ? `${value} (${requirements.length})` : `Requirements (${requirements.length})`} */}
          </Typography>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setRequirementsPopoverAnchor(e.currentTarget);
              setRequirementsPopoverRow(row);
            }}
            sx={{
              padding: '2px',
              color: 'info.main',
              '&:hover': { backgroundColor: 'action.hover' },
            }}
            aria-label="View requirements details"
          >
            <InfoOutlinedIcon sx={{ fontSize: '1rem' }} />
          </IconButton>
        </Box>
      );
    }

    // Special handling for enquiry number – optional Edit icon, History icon (when onHistoryClick), + clickable number
    if (column.id === 'enquiryNumber') {
      const showEdit = onEditClick && (typeof canEdit === 'function' ? canEdit(row) : canEdit);
      const showHistory = Boolean(onHistoryClick);
      return (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          {showEdit && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onEditClick(row);
              }}
              sx={{
                padding: '2px',
                color: 'primary.main',
                '&:hover': { backgroundColor: 'action.hover' },
              }}
              aria-label="Edit enquiry"
            >
              <EditIcon sx={{ fontSize: '1rem' }} />
            </IconButton>
          )}
          {showHistory && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onHistoryClick(e, row);
              }}
              sx={{
                padding: '2px',
                color: 'text.secondary',
                '&:hover': { backgroundColor: 'action.hover', color: 'primary.main' },
              }}
              aria-label="View enquiry history"
            >
              <HistoryIcon sx={{ fontSize: '1rem' }} />
            </IconButton>
          )}
          <Typography
            variant="body2"
            component="span"
            sx={{
              fontSize: { xs: '0.75rem', sm: '0.8125rem' },
              color: 'primary.main',
              cursor: 'pointer',
              textDecoration: 'underline',
              '&:hover': { color: 'primary.dark' },
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleCellClick(row, column.id, e);
            }}
          >
            {value || '-'}
          </Typography>
        </Box>
      );
    }

    // Special handling for action column
    if (column.id === 'action') {
      return (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            if (onActionClick) onActionClick(e, row);
          }}
          sx={{
            padding: { xs: '2px', sm: '4px' },
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <MoreVertIcon fontSize={isMobile ? 'small' : 'small'} />
        </IconButton>
      );
    }

    // Special handling for delete column
    if (column.id === 'delete') {
      return (
        <IconButton
          size="small"
          color="error"
          onClick={(e) => {
            e.stopPropagation();
            if (onDeleteClick) onDeleteClick(row);
          }}
          sx={{
            padding: { xs: '2px', sm: '4px' },
            '&:hover': {
              backgroundColor: 'error.light',
              '& .MuiSvgIcon-root': {
                color: 'error.dark',
              },
            },
          }}
        >
          <DeleteIcon fontSize={isMobile ? 'small' : 'small'} />
        </IconButton>
      );
    }

    // Format value if formatter exists
    if (column.format && typeof value !== 'undefined') {
      return column.format(value);
    }

    // Default: return value as string (truncate on mobile for long values)
    const displayValue = value || '-';
    return (
      <Typography 
        variant="body2" 
        component="span" 
        sx={{ 
          fontSize: { xs: '0.75rem', sm: '0.8125rem' },
          display: 'block',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: isMobile && column.id !== 'sn' ? 'nowrap' : 'normal',
          maxWidth: isMobile && column.id !== 'sn' ? '120px' : 'none'
        }}
        title={typeof displayValue === 'string' && displayValue.length > 20 ? displayValue : ''}
      >
        {displayValue}
      </Typography>
    );
  };

  return (
    <Paper 
      sx={{ 
        width: '100%', 
        overflow: 'hidden',
        borderRadius: { xs: 1, sm: 2 },
        boxShadow: { xs: 1, sm: 2 },
      }}
    >
      <TableContainer 
        sx={{ 
          maxHeight: { xs: 'calc(100vh - 240px)', sm: 'calc(100vh - 280px)' },
          overflowX: 'auto',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch', // Smooth scrolling on iOS
          '&::-webkit-scrollbar': {
            width: { xs: '4px', sm: '8px' },
            height: { xs: '4px', sm: '8px' },
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'background.default',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'divider',
            borderRadius: { xs: '2px', sm: '4px' },
            '&:hover': {
              backgroundColor: 'text.secondary',
            },
          },
        }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {visibleColumns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align || 'left'}
                  sx={{
                    minWidth: isMobile && column.id === firstColumnId ? { xs: 50, sm: column.minWidth } : column.minWidth,
                    width: isMobile && column.id === firstColumnId ? 'auto' : undefined,
                    position: isMobile && column.id === firstColumnId ? 'sticky' : 'relative',
                    left: isMobile && column.id === firstColumnId ? 0 : 'auto',
                    zIndex: isMobile && column.id === firstColumnId ? theme.zIndex.appBar + 1 : 'auto',
                    backgroundColor: isMobile && column.id === firstColumnId ? 'background.paper' : 'background.default',
                    fontWeight: 600,
                    fontSize: { xs: '0.7rem', sm: '0.75rem' },
                    textTransform: 'uppercase',
                    letterSpacing: { xs: '0.3px', sm: '0.5px' },
                    color: 'text.secondary',
                    borderBottom: '2px solid',
                    borderColor: 'divider',
                    padding: { xs: '8px 6px', sm: '10px 12px' },
                  }}
                >
                  {isMobile && column.id !== firstColumnId ? 
                    (column.shortLabel || column.label) : 
                    column.label
                  }
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={visibleColumns.length} align="center" sx={{ py: { xs: 4, sm: 6 } }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.8125rem' } }}>
                    No data available
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              data.map((row, index) => (
                <TableRow 
                  hover 
                  key={row.id || index}
                  sx={{
                    transition: 'background-color 0.15s ease',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  {visibleColumns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align || 'left'}
                      onClick={(e) => handleCellClick(row, column.id, e)}
                      sx={{
                        cursor: onCellClick && column.id !== 'action' && column.id !== 'delete' ? 'pointer' : 'default',
                        fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                        py: { xs: 0.75, sm: 1 },
                        px: { xs: 1, sm: 1.5 },
                        position: isMobile && column.id === firstColumnId ? 'sticky' : 'relative',
                        left: isMobile && column.id === firstColumnId ? 0 : 'auto',
                        zIndex: isMobile && column.id === firstColumnId ? theme.zIndex.appBar : 'auto',
                        backgroundColor: isMobile && column.id === firstColumnId ? 'background.paper' : 'transparent',
                        '&:hover': {
                          backgroundColor: onCellClick && column.id !== 'action' && column.id !== 'delete' ? 'action.selected' : 'transparent',
                        },
                      }}
                    >
                      {renderCellContent(row, column)}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {totalRows !== undefined && (
        <TablePagination
          component="div"
          count={totalRows}
          page={page}
          onPageChange={(e, newPage) => onPageChange(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => onRowsPerPageChange(parseInt(e.target.value, 10))}
          rowsPerPageOptions={[5, 10, 25, 50]}
          sx={{
            borderTop: '1px solid',
            borderColor: 'divider',
            '& .MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
              fontSize: { xs: '0.7rem', sm: '0.8125rem' },
            },
            '& .MuiTablePagination-toolbar': {
              padding: { xs: '4px 8px', sm: '8px 16px' },
              flexWrap: 'wrap',
              gap: { xs: 0.5, sm: 1 },
            },
            '& .MuiTablePagination-select': {
              padding: { xs: '4px 8px', sm: '6px 12px' },
            },
          }}
        />
      )}

      {/* Requirements info popover – full list from row data, no extra API */}
      <Popover
        open={Boolean(requirementsPopoverAnchor)}
        anchorEl={requirementsPopoverAnchor}
        onClose={() => {
          setRequirementsPopoverAnchor(null);
          setRequirementsPopoverRow(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { maxHeight: 360, maxWidth: 400, overflow: 'auto' } } }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
            Requirements
          </Typography>
          {requirementsPopoverRow && (() => {
            const reqs = requirementsPopoverRow.requirements || requirementsPopoverRow.requirement || [];
            if (reqs.length === 0) return <Typography variant="body2" color="text.secondary">No requirements</Typography>;
            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {reqs.map((req, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      p: 1.5,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'action.hover',
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {req.item_name || 'Unnamed Item'}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5, fontSize: '0.8125rem', color: 'text.secondary' }}>
                      <span>Unit: ₹{(req.item_unit_price ?? 0).toLocaleString()}</span>
                      <span>Qty: {req.item_quantity ?? 0}</span>
                      <span>Total: ₹{(req.item_total_price ?? 0).toLocaleString()}</span>
                    </Box>
                    {req.additional_details && (
                      <Typography variant="caption" sx={{ display: 'block', mt: 1, whiteSpace: 'pre-wrap' }}>
                        {req.additional_details}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            );
          })()}
        </Box>
      </Popover>
    </Paper>
  );
};

export default DataTable;
